//
// # HierarchyTreeRow
//
// A single row in the hierarchy tree. Shows an instance name and its target,
// with expand/collapse for lazy-loading children.
//
// - Click: select the instance in the viewport (top-level only)
// - Double-click: select and zoom to the instance
// - Right-click: context menu with "Open in New Tab" and "Zoom To"
// - Hover: highlight the instance on the canvas
//

import React, { useCallback, useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import type { schema } from "GenAlphaSchema";
import { useWorkspace } from "../../context";
import type { HierarchyEntry, ChildEntry } from "./useHierarchyData";
import { FadeIn } from "../common/FadeIn";
import {
  HierarchyContextMenu,
  type HierarchyContextMenuState,
} from "./HierarchyContextMenu";

interface HierarchyTreeRowProps {
  // Instance entry data
  readonly name: string;
  readonly id: number;
  readonly targetIdent: schema.Ident | null;
  readonly targetDisplayName: string;
  // Entity reference for selecting/highlighting in the viewport (top-level only)
  readonly entityRef: unknown;
  // Nesting depth for indentation
  readonly indentLevel: number;
  // Parent's path key (empty string for top-level rows)
  readonly parentKey: string;
  // Lazy-load function for expanding children
  readonly loadChildren: (
    targetIdent: schema.Ident
  ) => Promise<ReadonlyArray<ChildEntry>>;
  // Currently selected row key (for persistent highlight)
  readonly selectedKey: string | null;
  // Callback to select a row by its key
  readonly onSelect: (key: string) => void;
  // The active design's ident, threaded down from the panel. Passed as a prop
  // (rather than each row subscribing to the workspace snapshot) so that a
  // snapshot bump — which now happens on every edit via hierarchyVersion —
  // doesn't re-render the entire mounted tree. Rows only re-render when their
  // own props change.
  readonly activeIdent: schema.Ident | null;
}

function HierarchyTreeRowImpl({
  name,
  id,
  targetIdent,
  targetDisplayName,
  entityRef,
  indentLevel,
  parentKey,
  loadChildren,
  selectedKey,
  onSelect,
  activeIdent,
}: HierarchyTreeRowProps) {
  const workspace = useWorkspace();
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState<ReadonlyArray<ChildEntry> | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  // Whether this node can be expanded (has a target to load children from)
  const canExpand = targetIdent !== null;

  // Toggle expand/collapse
  const handleToggle = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canExpand || !targetIdent) return;

      if (expanded) {
        setExpanded(false);
        return;
      }

      if (children === null) {
        setLoading(true);
        const loaded = await loadChildren(targetIdent);
        setChildren(loaded);
        setLoading(false);
      }
      setExpanded(true);
    },
    [canExpand, targetIdent, expanded, children, loadChildren]
  );

  // Build a unique key for this row from its ancestry path.
  // Top-level: "X3:5", nested: "X3:5/X1:2/C0:0"
  const rowKey = parentKey ? `${parentKey}/${name}:${id}` : `${name}:${id}`;
  const isSelected = selectedKey === rowKey;

  // Click: select in hierarchy + select in viewport (top-level instances only)
  const handleClick = useCallback(() => {
    onSelect(rowKey);

    if (!entityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const content = editor.content;
    content.deselect();
    // entityRef is an Instance (schematic) or InstanceEntity (layout)
    const entity = entityRef as { highlight: (b: boolean) => unknown };
    content.addToSelection(entity as never);
  }, [workspace, entityRef, rowKey, onSelect]);

  // Double-click: select and zoom to fit
  const handleDoubleClick = useCallback(() => {
    if (!entityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const content = editor.content;
    content.deselect();
    content.addToSelection(entityRef as never);
    editor.fit();
  }, [workspace, entityRef]);

  // Hover enter: highlight on canvas
  const handleMouseEnter = useCallback(() => {
    if (!entityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const entity = entityRef as { highlight: (b: boolean) => unknown };
    entity.highlight(true);
    editor.requestRedraw();
  }, [workspace, entityRef]);

  // Hover leave: remove highlight, restore selection
  const handleMouseLeave = useCallback(() => {
    if (!entityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const entity = entityRef as { highlight: (b: boolean) => unknown };
    entity.highlight(false);

    // Restore highlight on selected entities
    for (const selected of editor.content.selection) {
      selected.highlight(true);
    }
    editor.requestRedraw();
  }, [workspace, entityRef]);

  // Right-click context menu state
  const [contextMenu, setContextMenu] =
    useState<HierarchyContextMenuState | null>(null);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  // Context menu action: open the target design in a new tab
  const handleOpenInNewTab = useCallback(() => {
    if (!targetIdent) return;
    workspace.openWithBreadcrumb(targetIdent, activeIdent);
  }, [workspace, targetIdent, activeIdent]);

  // Context menu action: select and zoom to this instance
  const handleZoomTo = useCallback(() => {
    if (!entityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const content = editor.content;
    content.deselect();
    content.addToSelection(entityRef as never);
    editor.fit();
  }, [workspace, entityRef]);

  const paddingLeft = 8 + indentLevel * 16;

  return (
    <>
      <div
        className={`hierarchy-row ${
          isSelected ? "hierarchy-row-selected" : ""
        } ${entityRef ? "" : "hierarchy-row-readonly"}`}
        style={{ paddingLeft }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
        data-hierarchy-key={rowKey}
      >
        {/* Expand/collapse chevron */}
        <span className="w-3 shrink-0" onClick={handleToggle}>
          {canExpand &&
            (expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
        </span>

        {/* Instance name */}
        <span className="hierarchy-name">{name}</span>

        {/* Target display name */}
        {targetDisplayName && (
          <span className="hierarchy-target">{targetDisplayName}</span>
        )}
      </div>

      {/* Loading indicator */}
      {loading && (
        <div
          className="hierarchy-loading"
          style={{ paddingLeft: paddingLeft + 16 }}
        >
          Loading...
        </div>
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <HierarchyContextMenu
          menu={contextMenu}
          onClose={() => setContextMenu(null)}
          onOpenInNewTab={handleOpenInNewTab}
          canOpenInNewTab={targetIdent !== null}
          onZoomTo={handleZoomTo}
          canZoomTo={entityRef !== null}
        />
      )}

      {/* Children (lazy-loaded).  Wrapped in FadeIn so the subtree
          animates in on expansion, matching the Explorer/Depot tree. */}
      {expanded && children && (
        <FadeIn>
          <div>
            {children.map((child) => (
              <HierarchyTreeRow
                // Stable name+id key (not the list index) so re-sorted or
                // updated child lists preserve each row's expansion state.
                key={`${child.name}:${child.id}`}
                name={child.name}
                id={child.id}
                targetIdent={child.targetIdent}
                targetDisplayName={child.targetDisplayName}
                entityRef={null}
                indentLevel={indentLevel + 1}
                parentKey={rowKey}
                loadChildren={loadChildren}
                selectedKey={selectedKey}
                onSelect={onSelect}
                activeIdent={activeIdent}
              />
            ))}
          </div>
        </FadeIn>
      )}
    </>
  );
}

// Memoized so a workspace-snapshot bump (now fired on every edit via
// hierarchyVersion) doesn't re-render the whole mounted tree. A row only
// re-renders when one of its own props actually changes — the top-level
// entries carry stable string/ident values, so an edit that doesn't touch a
// given instance leaves its row (and its expanded subtree) untouched. This
// keeps the panel cheap whether it's on screen or hidden behind another tab.
export const HierarchyTreeRow = React.memo(HierarchyTreeRowImpl);
